import {
  BaseSyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { Box } from '@material-ui/core';

import { ContainerType, DivContainerState, ImageContainerState, UniversalContainerState, Wysiwyg } from '@components';
import {
  DataVisualizationListItemSchema,
  deployment,
  experience,
  ExperienceDeployment,
  ExperienceItemMindset,
  ModuleDataVisualizationStation,
  moduleDetails,
  ModuleDisclosureStation,
  modules,
} from '@modules';
import { useAppDispatch } from '@store';
import { useHistory, parse, textTemplate } from '@utils';
import { Section, TOP_BAR_CTX_VALUE, TopBarContext } from '@layouts';
import { variables } from '@styles';

import { LibrariesPanelWrapper } from '../../TemplateView/LibrariesPanel/LibrariesPanelWrapper';

import { HTMLElementWithDocument } from '../../TemplateView/SampleTemplate/SampleTemplate.types';
import { getUniqueStyles } from '../../TemplateView/SampleTemplate/SampleTemplate.utils';
import { DELETE_MODULE_ICON_ID } from '../../TemplateView/SampleTemplate/SampleTemplate.const';

import {
  ChosenImage,
  DefaultChosenTreat,
  ElemInputActiveType,
  HTMLElementWithOriginalInnerHTML,
  HTMLImageElementWithOriginalInnerHTML,
  LeftPanelType,
  SingleModulesWrapper,
  SplitterData,
} from '../../TemplateView/TemplateView.types';
import { useStyles } from './TemplateEditor.styles';
import {
  DEFAULT_CHOSEN_IMAGE,
  DEFAULT_IMAGE_WRAPPER_HEIGHT,
  DEFAULT_IMAGE_WRAPPER_WIDTH,
  DEPLOYMENTS_MINDSET_DND_ID,
  LeftPanelTypes,
  Libraries,
  RightPanelElems,
  TreatmentBuilderContext,
} from '../../TemplateView/TemplateView.const';
import {
  convertWysiwygStylesToString,
  convertFromCamelToHyphenCase,
  getLibrariesWrapperTitle,
  getLibraryPanelComponent,
  getRedefinedWysiwygStyles,
  getSentenceCasedText,
  getBackgroundImageStyles,
  getUniversalInitialValues,
  transformStylesStringToObject,
  getDivInitialValues,
  getRedefinedButtonStyles,
  getButtonSettingsInitialValues,
  convertButtonStylesToString,
  removePreviousConditionalCommentNode,
  createButtonConditionalCommentVML,
  prependConditionalComment,
  terminateStylesString,
  onImageReady,
} from '../../TemplateView/TemplateView.utils';
import { content } from '@content';
import { ModuleModal, ModuleModalProps } from '@routes/TemplateView/SampleTemplate/ModuleModal';
import { TemplateEditorProps } from './TemplateEditor.props';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { ModuleHeader, ModuleHeaderProps } from '@routes/TemplateView/SampleTemplate/ModuleHeader';
import { ModuleSettings } from '@routes/TemplateView/SampleTemplate/ModuleSettings/ModuleSettings';
import { HTMLEditor } from '@components/Wysiwyg/HTMLEditor';
import { RenameModuleModal, RenameModuleModalFormValues } from '@routes/TemplateView';
import { MODULE_INTERACTION_STYLES, MODULE_INTERACTION_STYLES_ID } from '@routes/TemplateView/SampleTemplate';
import { getClearModuleHtmlExternally } from '@routes/TemplateView/SampleTemplate/ModuleModal/ModuleModal.hooks';
import {
  resizeModalIframe,
  positionIframeOverlay,
} from '@routes/TemplateView/SampleTemplate/ModuleModal/ModuleModal.utils';
import { TDVItem } from '@components/Wysiwyg/DVPanel/types';
import { messageReceiver, broadcaster } from '@utils/network/broadcast';
import {
  ButtonBackgroundImageState,
  ButtonSettingsState,
} from '@components/Wysiwyg/ButtonSettings/ButtonSettings.props';
import { MODULE_INTERACTION_ACTIVE_ID } from '@routes/TemplateView/SampleTemplate/ModuleModal/ModuleModal.const';

import { ViewportSize, ViewportSizeContext, ViewportWidth } from '@layouts/Private/Viewport';

/**
 Viewing Deployment -
 TemplateView route component
 @returns {JSX.Element}
 */

export const TemplateEditor = ({ module, setIsTemplateEditorVisible }: TemplateEditorProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {
    enterprise,
    businessUnit: businessUnitId = 0,
    deployment: depId,
    program,
    mailFile,
    template,
  } = history.query;

  const [chosenElemId, setChosenElemId] = useState('');
  const [chosenModuleElemId, setChosenModuleElemId] = useState('');
  const [chosenLibrary, setChosenLibrary] = useState<Libraries | null>(Libraries.IMAGES);
  const [rightPanelElem, setRightPanelElem] = useState<RightPanelElems>(RightPanelElems.MODULE_SETTINGS);
  const [editableTreatmentId, setEditableTreatmentId] = useState<number | null>(null);
  const [showAddTreatmentPanel, setShowAddTreatmentPanel] = useState(false);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [chosenModuleInnerHtml, setChosenModuleInnerHtml] = useState('');
  const [moduleIndex, setModuleIndex] = useState<number>(0);
  const [defaultChosenTreats, setDefaultChosenTreats] = useState<DefaultChosenTreat[]>([]);
  const [chosenElemStyles, setChosenElemStyles] = useState('');
  const [wysiwygContainerType, setWysiwygContainerType] = useState<ContainerType>('text');
  const [chosenImage, setChosenImage] = useState<ChosenImage>(DEFAULT_CHOSEN_IMAGE);
  const [chosenExperienceId, setChosenExperienceId] = useState<number | null>(null);
  const [singleModulesWrapper, setSingleModulesWrapper] = useState<SingleModulesWrapper>(null);
  const [isTreatmentTouched, setIsTreatmentTouched] = useState(false);
  const [chosenModuleId, setChosenModuleId] = useState<number | null>(null);
  const [currentExperienceId, setCurrentExperienceId] = useState<number | null>(null);
  const [isModuleChanged, setIsModuleChanged] = useState(false);
  const [isEditorChanged, setIsEditorChanged] = useState(false);
  const [showRenameModuleModal, setShowRenameModuleModal] = useState(false);
  const [newExperienceCloned, setNewExperienceCloned] = useState<SplitterData>({
    cloned: false,
    splitterId: null,
    experienceId: null,
  });
  const [deploymentsInfo, setDeploymentsInfo] = useState<ExperienceDeployment[]>([]);
  const [chosenDV, setChosenDV] = useState<DataVisualizationListItemSchema>();
  const [disclosure, setDisclosure] = useState<ModuleDisclosureStation | null>(null);
  const [experiencesIds, setExperiencesIds] = useState<number[]>([]);
  const [isTargetContentShown, setIsTargetContentShown] = useState(false);
  const [moduleHtml, setModuleHtml] = useState<string>(module.moduleHtml);
  const [elemInputActive, setElemInputActive] = useState<ElemInputActiveType>('disabled');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editMode, setEditMode] = useState('no-code');
  const [moduleDeployments, setModuleDeployments] = useState([]);
  const experienceData = experience.useItemData();
  const [showRemoveExperienceModal, setShowRemoveExperienceModal] = useState(false);
  const [showRemoveTreatmentModal, setShowRemoveTreatmentModal] = useState(false);
  const [showRemoveWithRevisions, setShowRemoveWithRevisions] = useState(false);
  const [moduleDataVisualizationStation, setModuleDataVisualizationStation] = useState<any>({});
  const [DVItems, setDVItems] = useState<TDVItem[]>([]);
  const [templateStyles, setTemplateStyles] = useState<HTMLCollectionOf<HTMLStyleElement> | null>(null);
  const [leftPanelType, setLeftPanelType] = useState<LeftPanelType>(LeftPanelTypes.IMAGES);

  // setup state for close/save buttons in right panel
  const [onModuleSave, setOnModuleSave] = useState<VoidFunction>(() => {});
  const [onModuleClose, setOnModuleClose] = useState<VoidFunction>(() => {});

  const disclosureRef = useRef<ModuleDisclosureStation | null>(null);
  const setDisclosureRef = useCallback(
    (setterFunc: (prev: ModuleDisclosureStation | null) => ModuleDisclosureStation | null) => {
      disclosureRef.current = setterFunc(disclosureRef.current);
    },
    [],
  );

  const buttonSettingsInitialValues = useMemo(
    () => getButtonSettingsInitialValues(chosenModuleElemId, transformStylesStringToObject(chosenElemStyles)),
    // [isModuleChanged, chosenModuleElemId],
    [chosenModuleElemId, chosenElemStyles],
  );

  const universalInitialValues = useMemo(
    () => getUniversalInitialValues(chosenModuleElemId, transformStylesStringToObject(chosenElemStyles)),
    // [isModuleChanged, chosenModuleElemId],
    [chosenModuleElemId, chosenElemStyles],
  );

  const contextValues = {
    chosenElemId,
    chosenModuleElemId,
    chosenExperienceId,
    experiencesIds,
    editableTreatmentId,
    showAddTreatmentPanel,
    showModuleEditor,
    chosenModuleInnerHtml,
    moduleIndex,
    defaultChosenTreats,
    chosenElemStyles,
    wysiwygContainerType,
    chosenLibrary,
    singleModulesWrapper,
    isTreatmentTouched,
    chosenModuleId,
    currentExperienceId,
    newExperienceCloned,
    deploymentsInfo,
    isModuleChanged,
    disclosure,
    isTargetContentShown,
    elemInputActive,
    cursorPosition,
    editMode,
    showRemoveExperienceModal,
    showRemoveTreatmentModal,
    showRemoveWithRevisions,
    moduleDataVisualizationStation,
    DVItems,
    deleteModuleIconId: DELETE_MODULE_ICON_ID,
    leftPanelType,
    onModuleClose,
    setOnModuleClose,
    onModuleSave,
    setOnModuleSave,
    setDVItems,
    setModuleDataVisualizationStation,
    setShowRemoveWithRevisions,
    setShowRemoveTreatmentModal,
    setShowRemoveExperienceModal,
    setEditMode,
    setDisclosure,
    setDeploymentsInfo,
    setExperiencesIds,
    setNewExperienceCloned,
    setCurrentExperienceId,
    setChosenModuleId,
    setIsTreatmentTouched,
    setSingleModulesWrapper,
    setChosenElemId,
    setChosenModuleElemId,
    setChosenExperienceId,
    setEditableTreatmentId,
    setShowAddTreatmentPanel,
    setShowModuleEditor,
    setChosenModuleInnerHtml,
    setModuleIndex,
    setDefaultChosenTreats,
    setChosenElemStyles,
    setWysiwygContainerType,
    setChosenLibrary,
    setIsModuleChanged,
    setIsTargetContentShown,
    setElemInputActive,
    setCursorPosition,
    setLeftPanelType,

    // SearchFilterSortUpload
  };
  const [
    {
      contentModuleName,

      // SearchFilterSortUpload
      contentCenterType,
      searchTerm,
    },
    setTopBarCtx,
  ] = useContext(TopBarContext);

  const divInitialValues = useMemo(
    () => getDivInitialValues(transformStylesStringToObject(chosenElemStyles)),
    // dependencies should be [chosenElemStyles, chosenModuleElemId]
    [chosenElemStyles, chosenModuleElemId],
  );

  /****************************************
   * Viewport Size Context
   */
  const { viewportSize, setViewportSize } = useContext(ViewportSizeContext);

  // set email viewport to Desktop size by default
  useEffect(() => {
    setViewportSize(ViewportSize.Desktop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { reason, type, source, destination } = result;

      // reorder mindsets
      if (
        reason === 'DROP' &&
        type === DEPLOYMENTS_MINDSET_DND_ID &&
        source.droppableId === DEPLOYMENTS_MINDSET_DND_ID &&
        destination?.droppableId === DEPLOYMENTS_MINDSET_DND_ID
      ) {
        await dispatch(experience.actions.moveMindset({ from: source.index, to: destination.index }));

        setIsTreatmentTouched(true);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    setDisclosureRef(() => disclosure);
  }, [setDisclosureRef, disclosure]);

  //Displaying the currently selected template if the template id in the query was changed
  useEffect(() => {
    (async () => {
      if (depId) {
        const result = await dispatch(deployment.thunk.getInfoEmailTemplate(depId));

        if (deployment.thunk.getInfoEmailTemplate.fulfilled.match(result) && result.payload) {
          const selectedTmplId = result.payload.deploymentEmailTemplates?.find((item) => item.isSelected)?.id;

          if (selectedTmplId && template !== selectedTmplId) {
            history.push('deployments', {
              enterprise,
              businessUnit: businessUnitId,
              deployment: depId,
              program,
              mailFile,
              template: selectedTmplId,
            });
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enterprise, businessUnitId, depId, program, mailFile, template, dispatch]);

  //removing items mindsets on template view loading
  useEffect(() => {
    dispatch(experience.actions.clearItemsMindsets());
  }, [dispatch]);

  useEffect(() => {
    setChosenModuleId(typeof module.id === 'number' ? module.id : module.editableModuleId);
  }, [module]);

  // getting Template by the deployment ID
  useEffect(() => {
    if (depId) {
      dispatch(deployment.thunk.getById(depId));
    }
  }, [depId, dispatch]);

  const handleChooseLibrary = useCallback(
    (libraryName: Libraries | null) => {
      setChosenLibrary(libraryName);
    },
    [setChosenLibrary],
  );

  const handleUniversalStyleChange = useCallback(
    (state: UniversalContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLElementWithOriginalInnerHTML;

      setChosenLibrary(Libraries.DATA_ATTRIBUTES);

      const redefinedWysiwygStyles = getRedefinedWysiwygStyles(state);

      redefinedWysiwygStyles.backgroundColor = redefinedWysiwygStyles.backgroundColor.toLowerCase();
      redefinedWysiwygStyles.color = redefinedWysiwygStyles.color.toLowerCase();

      const hyphenCasedStylesString = convertFromCamelToHyphenCase(
        convertWysiwygStylesToString(redefinedWysiwygStyles),
      );
      const uniqueStyles = getUniqueStyles(
        // `${chosenElemStyles}; ${hyphenCasedStylesString} \nborder-color: ${variables.color.primary.mainPurple};`,
        `${terminateStylesString(chosenElemStyles)} ${terminateStylesString(hyphenCasedStylesString)}`,
      );

      if (changingElem) {
        changingElem.setAttribute('style', uniqueStyles);

        if (redefinedWysiwygStyles.textTransform === 'sentence') {
          changingElem.innerHTML = getSentenceCasedText(changingElem?.innerHTML.trim());
        }
        if (redefinedWysiwygStyles.textTransform === 'unset') {
          changingElem.innerHTML = changingElem.getAttribute('originalInnerHTML') || changingElem.innerHTML;
        }
      }

      resizeModalIframe(iframe as HTMLIFrameElement, true);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && changingElem) {
        positionIframeOverlay(activeOverlay, changingElem);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chosenModuleElemId],
  );

  const handleButtonStyleChange = useCallback(
    (state: ButtonSettingsState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLElementWithOriginalInnerHTML;

      setChosenLibrary(Libraries.DATA_ATTRIBUTES);

      const redefinedButtonStyles = getRedefinedButtonStyles(state);

      // if the background image is not set, empty or set to 'none',
      // check for and remove the previous conditional comment code node
      removePreviousConditionalCommentNode(changingElem);

      const stylesArray = chosenElemStyles.split(';').map((item) => item.trim());

      redefinedButtonStyles.backgroundColor = redefinedButtonStyles.backgroundColor?.toLowerCase();
      redefinedButtonStyles.borderColor = redefinedButtonStyles.borderColor?.toLowerCase();
      redefinedButtonStyles.color = redefinedButtonStyles.color?.toLowerCase();

      const msoHideStyle = 'mso-hide:all;';
      let includeMsoHideStyle = false;

      if (redefinedButtonStyles) {
        const hyphenCasedStylesString = convertButtonStylesToString(redefinedButtonStyles);
        const uniqueStyles = getUniqueStyles(
          `${terminateStylesString(stylesArray.join(';'))} ${terminateStylesString(hyphenCasedStylesString)}`,
        );

        if (changingElem) {
          changingElem.setAttribute('style', uniqueStyles);

          if (redefinedButtonStyles.textTransform === 'sentence') {
            changingElem.innerHTML = getSentenceCasedText(changingElem?.innerHTML.trim());
          }
          if (redefinedButtonStyles.textTransform === 'unset') {
            changingElem.innerHTML = changingElem.getAttribute('originalInnerHTML') || changingElem.innerHTML;
          }
        }

        // update the conditional comment and VML if there is a background image
        const buttonBackground = redefinedButtonStyles.backgroundImage || '';

        if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
          // create the VML for Outlook
          const vml = createButtonConditionalCommentVML(changingElem);

          if (vml) {
            // prepend the VML comment to the button element
            prependConditionalComment(changingElem, vml);

            includeMsoHideStyle = true;
          }
        }
      }

      if (includeMsoHideStyle) {
        // set the mso-hide style to the button element
        changingElem.setAttribute(
          'style',
          getUniqueStyles(`${terminateStylesString(changingElem.getAttribute('style'))} ${msoHideStyle}`),
        );
      } else {
        // remove the mso-hide style from the button element
        changingElem.setAttribute(
          'style',
          getUniqueStyles(changingElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
        );
      }

      resizeModalIframe(iframe as HTMLIFrameElement, true);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && changingElem) {
        positionIframeOverlay(activeOverlay, changingElem);
      }
    },
    // xeslint-disable-next-line react-hooks/exhaustive-deps
    // [chosenModuleElemId],
    [chosenElemStyles, chosenModuleElemId],
  );

  const handleImageStyleChange = useCallback(
    (state: ImageContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const imageElem = iframe.contentDocument.getElementById(
        chosenModuleElemId,
      ) as HTMLImageElementWithOriginalInnerHTML;

      if (chosenModuleElemId !== state?.chosenModuleElemId || !imageElem) {
        return;
      }

      const imageStyles = imageElem.getAttribute('style') || '';

      const filteredStyles = imageStyles
        .split(';')
        .map((item) => item.replace('\n', ''))
        .excludeStrs(['width:', 'height:'])
        .join(';\n');

      if (imageElem && ([Libraries.IMAGES, Libraries.DATA_VISUALIZATIONS].includes(chosenLibrary!) || !chosenLibrary)) {
        imageElem.setAttribute('src', state?.imageSrc || '');
        imageElem.setAttribute('style', filteredStyles);
      }

      if (imageElem) {
        imageElem.setAttribute('width', state?.width || '');
        imageElem.setAttribute('height', state?.height || '');
      }

      onImageReady(imageElem, () => {
        resizeModalIframe(iframe as HTMLIFrameElement, true);

        // adjust the position of the active overlay
        const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
        if (activeOverlay && imageElem) {
          positionIframeOverlay(activeOverlay, imageElem);
        }
      });
    },
    [chosenLibrary, chosenModuleElemId],
  );

  const handleDVStyleChange = useCallback(
    (state: { width: number; height: number }) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const imageElem = iframe.contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (imageElem) {
        imageElem.setAttribute('width', state?.width.toString() || '');
        imageElem.setAttribute('height', state?.height.toString() || '');
      }

      resizeModalIframe(iframe as HTMLIFrameElement, true);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && imageElem) {
        positionIframeOverlay(activeOverlay, imageElem);
      }
    },
    [chosenModuleElemId],
  );

  const handleLinkChange = useCallback(
    (href: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const activeElement = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // logic for accessing the link URL based on the tag name
      const tagName = activeElement?.tagName.toLowerCase() ?? '';

      // if tagname is A, set the href and temphref attributes
      if (tagName === 'a') {
        activeElement?.setAttribute('href', href);
        activeElement?.setAttribute('temphref', href);

        // update the conditional comment if the data-element-type is button
        if (activeElement?.getAttribute('data-element-type') === 'button') {
          // remove previous conditional comment node if exists
          removePreviousConditionalCommentNode(activeElement);

          const buttonBackground = activeElement?.style.backgroundImage || '';
          const msoHideStyle = 'mso-hide:all;';
          let includeMsoHideStyle = false;

          // if the background image is set, prepend the conditional comment code
          if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
            // create the VML for Outlook
            const vml = createButtonConditionalCommentVML(activeElement);

            if (vml) {
              // prepend the VML comment to the button element
              prependConditionalComment(activeElement, vml);

              includeMsoHideStyle = true;
            }
          }

          if (includeMsoHideStyle) {
            // set the mso-hide style to the button element
            activeElement.setAttribute(
              'style',
              getUniqueStyles(`${terminateStylesString(activeElement.getAttribute('style'))} ${msoHideStyle}`),
            );
          } else {
            // remove the mso-hide style from the button element
            activeElement.setAttribute(
              'style',
              getUniqueStyles(activeElement.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
            );
          }
        }

        // remove the anchor tag if the href is empty and its not a button
        if ((!href || href.trim() === '') && activeElement?.getAttribute('data-element-type') !== 'button') {
          const parent = activeElement?.parentElement;
          if (!parent) {
            return;
          }

          while (activeElement.firstChild) {
            parent.insertBefore(activeElement.firstChild, activeElement);
          }
          // Remove the anchor itself
          parent.removeChild(activeElement);
        }
      }
      // if tagname is span or img, look for parent A
      else if (tagName === 'span' || tagName === 'img') {
        const parentElement = activeElement?.parentElement;

        // if there is an href value, update or create the A element
        if (href || href.trim() !== '') {
          if (parentElement?.tagName.toLowerCase() === 'a') {
            parentElement?.setAttribute('href', href);
            parentElement?.setAttribute('temphref', href);
          }
          // otherwise create the A element
          else {
            const linkElem = document.createElement('a');
            linkElem.setAttribute('href', href);
            linkElem.setAttribute('temphref', href);

            // get the active elements calculated color style
            const color = window.getComputedStyle(activeElement).color;
            linkElem.style.color = color;

            activeElement?.replaceWith(linkElem);
            linkElem.appendChild(activeElement);
            return;
          }
        }
        // else the href is empty, remove the A element
        else {
          if (parentElement && parentElement.tagName.toLowerCase() === 'a') {
            const grandParent = parentElement.parentElement;
            if (!grandParent) {
              return;
            }

            while (parentElement.firstChild) {
              grandParent.insertBefore(parentElement.firstChild, parentElement);
            }
            // Remove the anchor itself
            grandParent.removeChild(parentElement);
          }
        }
      }
      // if tagname is div, h1-h6, p, look for first child A
      else if (['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
        const firstChild = activeElement?.firstElementChild;

        // if there is an href value, update or create the A element
        if (href || href.trim() !== '') {
          if (firstChild?.tagName.toLowerCase() === 'a') {
            firstChild.setAttribute('href', href);
            firstChild.setAttribute('temphref', href);
          }
          // otherwise create the child A element
          else {
            const linkElem = document.createElement('a');
            linkElem.setAttribute('href', href);
            linkElem.setAttribute('temphref', href);

            // get the active elements calculated color style
            const color = window.getComputedStyle(activeElement).color;
            linkElem.style.color = color;

            // get the active elements children and append them to the link element
            const childNodes = Array.from(activeElement.childNodes);
            childNodes.forEach((childNode) => {
              linkElem.appendChild(childNode);
            });

            // replace the active element children with the new link element
            activeElement?.replaceChildren(linkElem);
          }
        }
        // else the href is empty, remove the A element
        else {
          if (firstChild && firstChild.tagName.toLowerCase() === 'a') {
            while (firstChild.firstChild) {
              activeElement.insertBefore(firstChild.firstChild, firstChild);
            }
            // Remove the anchor itself
            activeElement.removeChild(firstChild);
          }
        }
      }
    },
    [chosenModuleElemId],
  );

  const handleAltTextChange = useCallback(
    (altText: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const curElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (curElem.tagName.toLowerCase() === 'img') {
        if (!altText || altText.trim() === '') {
          curElem.removeAttribute('alt');
        } else {
          curElem.setAttribute('alt', altText);
        }
      }
    },
    [chosenModuleElemId],
  );

  // Handle changes to the element title text and aria-label attributes
  const handleTitleChange = useCallback(
    (title: string) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // logic for accessing the link URL based on the tag name
      const tagName = changingElem?.tagName.toLowerCase() ?? '';
      let targetAnchor: HTMLElement | null = null;

      // if tagname is A
      if (tagName === 'a') {
        targetAnchor = changingElem;

        changingElem.setAttribute('title', title);
        changingElem.setAttribute('aria-label', title);

        // if tagname is A and data-element-type is button
        if (changingElem?.getAttribute('data-element-type') === 'button') {
          // remove previous conditional comment node if exists
          removePreviousConditionalCommentNode(changingElem);

          const buttonBackground = changingElem?.style.backgroundImage || '';
          const msoHideStyle = 'mso-hide:all;';
          let includeMsoHideStyle = false;

          // if the background image is set, prepend the conditional comment code
          if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
            // create the VML for Outlook
            const vml = createButtonConditionalCommentVML(changingElem);

            if (vml) {
              // prepend the VML comment to the button element
              prependConditionalComment(changingElem, vml);

              includeMsoHideStyle = true;
            }
          }

          if (includeMsoHideStyle) {
            // set the mso-hide style to the button element
            changingElem.setAttribute(
              'style',
              getUniqueStyles(`${terminateStylesString(changingElem.getAttribute('style'))} ${msoHideStyle}`),
            );
          } else {
            // remove the mso-hide style from the button element
            changingElem.setAttribute(
              'style',
              getUniqueStyles(changingElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
            );
          }
        }
      }
      // If tagname is span or img, look for parent A
      else if (tagName === 'span' || tagName === 'img') {
        const parentA = changingElem?.parentElement as HTMLElement | null;
        if (parentA?.tagName.toLowerCase() === 'a') {
          targetAnchor = parentA;
        }
      }
      // if tagname is h1-h6, p, look for first child A
      else if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'].includes(tagName)) {
        const firstChild = changingElem?.firstElementChild as HTMLElement | null;

        if (firstChild?.tagName.toLowerCase() === 'a') {
          targetAnchor = firstChild;
        }
      }

      if (targetAnchor) {
        // set the title and aria-label attributes for the changing <A> element
        if (!title || title.trim() === '') {
          targetAnchor.removeAttribute('title');
          targetAnchor.removeAttribute('aria-label');
        } else {
          targetAnchor.setAttribute('title', title);
          targetAnchor.setAttribute('aria-label', title);
        }
      }
    },
    [chosenModuleElemId],
  );

  const handleButtonBGImageChange = useCallback(
    (state: ButtonBackgroundImageState) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const buttonElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      if (!buttonElem) {
        return;
      }

      const buttonElemStyles = buttonElem.getAttribute('style');
      const buttonOffsetHeight = buttonElem.offsetHeight;
      const buttonOffsetWidth = buttonElem.offsetWidth;

      // set the background styles for the button element
      buttonElem.setAttribute(
        'style',
        getUniqueStyles(
          `${terminateStylesString(buttonElemStyles)} ${getBackgroundImageStyles(
            state,
            buttonOffsetWidth,
            buttonOffsetHeight,
          )}`,
        ),
      );

      // remove previous conditional comment node if exists
      removePreviousConditionalCommentNode(buttonElem);

      // create the VML for Outlook
      const vml = createButtonConditionalCommentVML(buttonElem);
      let msoHideStyle = '';

      if (vml) {
        // prepend the VML comment to the button element
        prependConditionalComment(buttonElem, vml);
        msoHideStyle = 'mso-hide:all;';
      }

      // set the mso-hide style to the button element
      buttonElem.setAttribute(
        'style',
        getUniqueStyles(`${terminateStylesString(buttonElem.getAttribute('style'))} ${msoHideStyle}`),
      );
    },
    [chosenModuleElemId],
  );

  const handleDivStyleChange = useCallback(
    (state: DivContainerState) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const divElem = iframe.contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;
      let divElemStyles = divElem?.getAttribute('style');
      const divOffsetWidth = divElem?.offsetWidth;
      const divOffsetHeight = divElem?.offsetHeight;

      if (divElemStyles && !divElemStyles.trim().endsWith(';')) {
        divElemStyles = divElemStyles + ';';
      }

      divElem?.setAttribute(
        'style',
        getUniqueStyles(
          `${terminateStylesString(divElemStyles)} ${getBackgroundImageStyles(state, divOffsetWidth, divOffsetHeight)}`,
        ),
      );

      resizeModalIframe(iframe as HTMLIFrameElement, true);

      // adjust the position of the active overlay
      const activeOverlay = iframe.contentDocument.getElementById(MODULE_INTERACTION_ACTIVE_ID);
      if (activeOverlay && divElem) {
        positionIframeOverlay(activeOverlay, divElem);
      }
    },
    [chosenModuleElemId],
  );

  const handleChooseImage = useCallback(
    (image: any) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // only attempt to change DV or Image if a module element has been selected and found in DOM
      if (changingElem) {
        // only allow changing image for img, div, and a tags. For div and a tags that are buttons,
        // the image will be set as a background image
        const validTags = ['img', 'div', 'a'];
        if (!validTags.includes(changingElem.tagName.toLowerCase())) {
          return;
        }

        if (image.thumbnailPath) {
          // if the chosen element is not an img tag, do not update with data visualization
          if (changingElem.tagName.toLowerCase() !== 'img') {
            return;
          }

          // Update selected DV image
          const payload = {
            chosenModuleElemId: chosenModuleElemId,
            height: image.height,
            imageSrc: image.thumbnailPath,
            name: image.name,
            width: image.width,
          };

          setChosenDV(image);
          setChosenImage(payload as any);
          setRightPanelElem(RightPanelElems.WYSIWYG_DATA_VISUAL);

          handleImageStyleChange(payload);

          // add data visualization attributes
          changingElem.setAttribute('data-visualization-station', 'true');
          changingElem.setAttribute('dataVisualId', image.id);
          changingElem.setAttribute('dataVisualEmbdedCode', image.embedCode);
          changingElem.setAttribute('isMaxScaleEnabled', image.isMaxScaleEnabled);
        } else {
          setChosenImage(image);

          switch (changingElem.tagName.toLowerCase()) {
            case 'img':
              handleImageStyleChange(image);
              setRightPanelElem(RightPanelElems.WYSIWYG_IMAGE);
              break;
            case 'div':
              handleDivStyleChange(image);
              // setRightPanelElem(RightPanelElems.WYSIWYG_DIV);
              break;
            case 'a': // only when element-type=button
              if (changingElem.getAttribute('data-element-type') === 'button') {
                handleButtonBGImageChange(image);
                // setRightPanelElem(RightPanelElems.BUTTON_SETTINGS);
              }
              break;
            default:
              return;
          }

          // remove data visualization attributes
          changingElem.removeAttribute('data-visualization-station');
          changingElem.removeAttribute('dataVisualId');
          changingElem.removeAttribute('dataVisualEmbdedCode');
          changingElem.removeAttribute('isMaxScaleEnabled');
          setModuleDataVisualizationStation(null);
        }
        setIsModuleChanged(true);
      }
    },
    [
      chosenModuleElemId,
      // rightPanelElem,
      handleButtonBGImageChange,
      handleDivStyleChange,
      handleImageStyleChange,
      setChosenImage,
      setChosenDV,
      setModuleDataVisualizationStation,
    ],
  );

  const onEditorChange = (value: string) => {
    module.moduleHtml = value;
    setIsModuleChanged(true);
    setIsEditorChanged(true);
    setModuleHtml(value);
  };

  const handleDimensionsChange = ([width, height]: [number, number]) => {
    const payload = {
      height,
      width,
    };

    handleDVStyleChange(payload);
  };

  const handleModuleClose = useCallback(() => {
    setIsTemplateEditorVisible(false);
    setEditMode('no-code');
    setDisclosure(null);

    setTimeout(() => {
      broadcaster.postMessage('refresh');
    }, 500);
  }, [setIsTemplateEditorVisible]);

  const handleUpdateModule = useCallback(
    async (templateName?: string, moduleInfo?: ExperienceItemMindset) => {
      const iframe = document.getElementById('module-modal') as HTMLElementWithDocument;
      const dcs = disclosureRef.current;

      if (iframe?.contentDocument) {
        iframe.contentDocument.body.style.margin = 'initial';
        iframe.contentDocument.body.style.border = 'initial';
        iframe.contentDocument.body.style.borderRadius = 'initial';
      }

      const curModuleHtml = getClearModuleHtmlExternally('module-modal', chosenModuleElemId);
      let curModulePayload: ExperienceItemMindset;

      if (!moduleInfo) {
        const curModuleInfo = await dispatch(modules.thunk.getModuleInfo({ module: chosenModuleId! }));
        curModulePayload = curModuleInfo.payload as unknown as ExperienceItemMindset;
      } else {
        curModulePayload = moduleInfo;
      }

      let curModuleDataVisualizationStation = null as any;

      if (moduleDataVisualizationStation && moduleDataVisualizationStation[chosenModuleId!]) {
        curModuleDataVisualizationStation = {
          ...moduleDataVisualizationStation[chosenModuleId!],
          id: (curModulePayload as any).moduleDataVisualizationStation?.id,
        };
      }

      if (module) {
        let result: any = await dispatch(
          moduleDetails.thunk.save({
            details: {
              templateName,
              id: module.editableModuleId ?? module.id,
              moduleHtml: curModuleHtml.moduleHtml
                .replaceAll(/\n/gm, '')
                .replaceAll('temphref', 'href')
                .replace(/\s+/g, ' '),
            },
            isDirty: true,
          }),
        );

        if (dcs?.disclosureCopy) {
          result = await dispatch(
            modules.thunk.replaceModuleDisclosureStation({
              module: chosenModuleId!,
              payload: {
                moduleDisclosureStation: {
                  ...(result.payload.moduleDisclosureStation ?? curModulePayload.moduleDisclosureStation ?? {}),
                  ...dcs,
                  id:
                    result.payload.moduleDisclosureStation?.id ??
                    curModulePayload.moduleDisclosureStation?.id ??
                    dcs?.id,
                },
              },
            }),
          );
        } else if (result.payload.moduleDisclosureStation && !dcs?.disclosureCopy) {
          result = await dispatch(
            modules.thunk.replaceModuleDisclosureStation({
              module: chosenModuleId!,
              payload: {
                moduleDisclosureStation: null,
              },
            }),
          );
        }

        curModulePayload = result?.payload as unknown as ExperienceItemMindset;

        if (curModuleDataVisualizationStation) {
          let settings =
            curModuleDataVisualizationStation?.settings ?? curModulePayload.moduleDataVisualizationStation?.settings;

          if (templateName && curModuleDataVisualizationStation?.settings) {
            settings = curModulePayload.moduleDataVisualizationStation?.settings.filter((setting) =>
              curModuleDataVisualizationStation?.settings.find(
                (settingItem: any) => settingItem.label === setting.label,
              ),
            ) as any[];

            settings = curModuleDataVisualizationStation.settings
              ? (settings ?? []).concat(
                  curModuleDataVisualizationStation.settings.filter((setting: any) => !setting.id),
                )
              : (settings ?? []);
          }

          await dispatch(
            modules.thunk.replaceModuleDataVisualizationStation({
              module: chosenModuleId!,
              payload: {
                moduleDataVisualizationStation: {
                  ...curModuleDataVisualizationStation,
                  id: curModulePayload.moduleDataVisualizationStation?.id,
                  settings,
                },
              },
            }),
          );
        } else if (!moduleDataVisualizationStation) {
          await dispatch(
            modules.thunk.replaceModuleDataVisualizationStation({
              module: chosenModuleId!,
              payload: {
                moduleDataVisualizationStation: null,
              },
            }),
          );
        }

        await dispatch(
          modules.actions.updateLibraryItemDisclosure({
            id: curModulePayload.id,
            moduleDisclosureStation: {
              ...(curModulePayload.moduleDisclosureStation ?? {}),
              ...dcs,
            },
          }),
        );

        dispatch(modules.actions.updateModule(result.payload));
        handleModuleClose();
      }
    },
    [chosenModuleElemId, chosenModuleId, dispatch, handleModuleClose, module, moduleDataVisualizationStation],
  );

  const handleModuleSave = useCallback<ModuleHeaderProps['onSave']>(
    async () => {
      const response = await dispatch(modules.thunk.getModuleInfo({ module: module.editableModuleId ?? module.id }));

      setModuleDeployments((response.payload as any)?.deployments);

      if (
        (response.payload as any)?.deployments?.length > 1 ||
        (response.payload as any)?.hasBeenUsedAroundTemplates ||
        experienceData?.type === 'revision' ||
        experienceData?.revisions.length
      ) {
        setShowRenameModuleModal(true);
      } else {
        handleUpdateModule(undefined, response.payload as any);
      }
    },
    // [moduleDataVisualizationStation, chosenModuleId, handleUpdateModule, setModuleDeployments]
    [
      dispatch,
      module.editableModuleId,
      module.id,
      experienceData?.type,
      experienceData?.revisions.length,
      handleUpdateModule,
    ],
  );

  const getRightPanelElem = () => {
    if (editMode === 'html') {
      return (
        <HTMLEditor
          isTemplateEditor={true}
          moduleHtml={module.moduleHtml}
          moduleName={module.templateName}
          onEditorChange={onEditorChange}
        />
      );
    }

    switch (rightPanelElem) {
      case RightPanelElems.BUTTON_SETTINGS:
        return (
          <Wysiwyg.ButtonSettings
            isModuleChanged={isModuleChanged}
            setIsModuleChanged={setIsModuleChanged}
            initialValues={buttonSettingsInitialValues}
            chosenImage={chosenImage}
            containerType={wysiwygContainerType}
            onChange={handleButtonStyleChange}
            onLinkChange={handleLinkChange}
            onTitleChange={handleTitleChange}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
      case RightPanelElems.WYSIWYG_UNIVERSAL:
        return (
          <Wysiwyg.UniversalContainer
            isModuleChanged={isModuleChanged}
            setIsModuleChanged={setIsModuleChanged}
            initialValues={universalInitialValues}
            containerType={wysiwygContainerType}
            onChange={handleUniversalStyleChange}
            onLinkChange={handleLinkChange}
            onTitleChange={handleTitleChange}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
      case RightPanelElems.WYSIWYG_IMAGE:
        return (
          <Wysiwyg.ImageContainer
            isModuleChanged={isModuleChanged}
            setIsModuleChanged={setIsModuleChanged}
            setChosenLibrary={setChosenLibrary}
            chosenImage={chosenImage}
            onChange={handleImageStyleChange}
            onLinkChange={handleLinkChange}
            onTitleChange={handleTitleChange}
            onAltTextChange={handleAltTextChange}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
      case RightPanelElems.WYSIWYG_DATA_VISUAL:
        return (
          <Wysiwyg.DVPanel
            chosenDV={chosenDV!}
            isModuleChanged={isModuleChanged}
            setIsModuleChanged={setIsModuleChanged}
            setChosenLibrary={setChosenLibrary}
            onImageChange={handleImageStyleChange}
            onDimensionsChange={handleDimensionsChange}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
      case RightPanelElems.WYSIWYG_DIV:
        return (
          <Wysiwyg.DivContainer
            isModuleChanged={isModuleChanged}
            setIsModuleChanged={setIsModuleChanged}
            initialValues={divInitialValues}
            chosenImage={chosenImage}
            onChange={handleDivStyleChange}
            setChosenLibrary={setChosenLibrary}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
      case RightPanelElems.MODULE_SETTINGS:
        return (
          <ModuleSettings
            module={{ ...module }}
            onEditorChange={onEditorChange}
            isModuleChanged={isModuleChanged}
            onModuleClose={handleModuleClose}
            onModuleSave={handleModuleSave}
          />
        );
    }
  };

  useEffect(() => {
    const moduleElem = document?.getElementById('module-modal') as HTMLElementWithDocument;
    const imageElem = moduleElem?.contentDocument.getElementById(
      chosenModuleElemId,
    ) as HTMLElementWithOriginalInnerHTML;

    setChosenImage(DEFAULT_CHOSEN_IMAGE);

    if (imageElem && chosenModuleElemId.indexOf('img') !== -1) {
      setTimeout(() => {
        const imageSrc = imageElem.getAttribute('src');
        const width = Math.round(imageElem.getBoundingClientRect().width);
        const height = Math.round(imageElem.getBoundingClientRect().height);
        const dataVisualId = Number(imageElem.getAttribute('dataVisualId'));
        const dataVisualEmbedCode = imageElem.getAttribute('dataVisualEmbdedCode');
        const isMaxScaleEnabled = imageElem.getAttribute('isMaxScaleEnabled') === 'false' ? false : true;
        const linkParent = imageElem.parentElement?.tagName === 'A' ? imageElem.parentElement : null;
        const linkValue = linkParent?.getAttribute('href') ?? linkParent?.getAttribute('temphref') ?? '';
        const linkTitleValue = linkParent?.getAttribute('title') ?? '';
        const altTextValue = imageElem.getAttribute('alt') ?? '';

        if (chosenDV?.id !== dataVisualId) {
          setChosenDV({
            id: dataVisualId ?? 0,
            embedCode: dataVisualEmbedCode,
            isMaxScaleEnabled,
            width,
            imageSrc: '',
            height,
          } as any);
        }

        setChosenImage({
          id: null,
          chosenModuleElemId,
          imageSrc,
          width: width ? +width : DEFAULT_IMAGE_WRAPPER_WIDTH,
          height: height ? +height : DEFAULT_IMAGE_WRAPPER_HEIGHT,
          name: null,
          link: linkValue,
          title: linkTitleValue,
          altText: altTextValue,
        });
      }, 150);
    } else if (imageElem && chosenModuleElemId.indexOf('div') !== -1) {
      const styleAttr = imageElem.getAttribute('style') ?? '';
      const bgImageMatch = styleAttr.match(/background-image:\s*([^;]+)/);
      const imageSrc = bgImageMatch ? bgImageMatch[1].trim() : 'none';
      const width = Math.round(imageElem.getBoundingClientRect().width);
      const height = Math.round(imageElem.getBoundingClientRect().height);

      setChosenImage({
        id: null,
        chosenModuleElemId,
        imageSrc,
        width: width ? +width : DEFAULT_IMAGE_WRAPPER_WIDTH,
        height: height ? +height : DEFAULT_IMAGE_WRAPPER_HEIGHT,
        name: null,
        link: null,
        title: null,
        altText: null,
      });
    } else {
      setChosenImage(DEFAULT_CHOSEN_IMAGE);
    }
  }, [chosenModuleElemId, chosenDV, setChosenImage]);

  useEffect(() => {
    setTopBarCtx((prev) => ({
      ...prev,
      variant: 'section',
      sectionName: textTemplate(content.entityManager, { entity: content.content }),
    }));

    return () => {
      setTopBarCtx({ ...TOP_BAR_CTX_VALUE, contentCenterType, searchTerm });
      setModuleDeployments([]);
    };
  }, [setTopBarCtx, setModuleDeployments, contentCenterType, searchTerm]);

  const handleModuleChange = useCallback<ModuleModalProps['onChange']>(
    (html, dirty, getClearModuleHtml) => {
      const { contentDocument } = document.getElementById('module-modal') as HTMLElementWithDocument;
      const changingElem = contentDocument.getElementById(chosenModuleElemId) as HTMLElementWithOriginalInnerHTML;

      // const clearModuleHtml = getClearModuleHtmlExternally('module-modal', chosenModuleElemId).moduleHtml;
      const clearModuleHtml = getClearModuleHtml().moduleHtml;

      if (
        rightPanelElem === RightPanelElems.MODULE_SETTINGS &&
        moduleHtml !== clearModuleHtml &&
        clearModuleHtml !== '<body></body>' &&
        moduleHtml !== module.moduleHtml
      ) {
        setModuleHtml(clearModuleHtml);
      }
      if (elemInputActive === 'completed' && moduleHtml !== clearModuleHtml) {
        setModuleHtml(clearModuleHtml);
        setElemInputActive('disabled');
      }
    },
    // [chosenModuleElemId, rightPanelElem, moduleHtml, module.moduleHtml, elemInputActive],
    [chosenModuleElemId, rightPanelElem, moduleHtml, module.moduleHtml, elemInputActive],
  );

  const handleInputBlur = useCallback(
    (parsedOriginalElem: HTMLElement, getClearModuleHtml: () => { moduleHtml: string; filledAttrValue: string }) => {
      // const clearModuleHtml = getClearModuleHtml().moduleHtml;
      // setModuleHtml(clearModuleHtml);

      // get selected module element from chosenModuleElemId
      const moduleElem = document?.getElementById('module-modal') as HTMLElementWithDocument;
      const selectedElem = parsedOriginalElem; // moduleElem?.contentDocument.getElementById(chosenModuleElemId);

      // if the selected module and the current module is a button with a background, update the conditional comment
      if (selectedElem && selectedElem.getAttribute('data-element-type') === 'button') {
        removePreviousConditionalCommentNode(selectedElem);

        const msoHideStyle = 'mso-hide:all;';
        let includeMsoHideStyle = false;

        const buttonBackground = selectedElem?.style.backgroundImage || '';

        if (buttonBackground && buttonBackground !== 'none' && buttonBackground.includes('url(')) {
          // create the VML for Outlook
          const vml = createButtonConditionalCommentVML(selectedElem);

          if (vml) {
            // prepend the VML comment to the button element
            prependConditionalComment(selectedElem, vml);

            includeMsoHideStyle = true;
          }
        }

        if (includeMsoHideStyle) {
          // set the mso-hide style to the button element
          selectedElem.setAttribute(
            'style',
            getUniqueStyles(`${terminateStylesString(selectedElem.getAttribute('style'))} ${msoHideStyle}`),
          );
        } else {
          // remove the mso-hide style from the button element
          selectedElem.setAttribute(
            'style',
            getUniqueStyles(selectedElem.getAttribute('style')?.replace(/mso-hide:all;/g, '') || ''),
          );
        }
      }
    },
    // [chosenModuleElemId, rightPanelElem, moduleHtml, module.moduleHtml, elemInputActive],
    [],
  );

  const handleRenameModuleModalClose = useCallback(() => {
    setShowRenameModuleModal(false);
  }, [setShowRenameModuleModal]);

  const handleConfirmRenameModule = useCallback(
    async (nameData: RenameModuleModalFormValues) => {
      handleUpdateModule(nameData.name);
      handleRenameModuleModalClose();
    },
    // [moduleDataVisualizationStation, chosenModuleId, handleUpdateModule, handleRenameModuleModalClose],
    [handleUpdateModule, handleRenameModuleModalClose],
  );

  return (
    <>
      <Box className={styles.templateView}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <TreatmentBuilderContext.Provider value={contextValues}>
            <Section className={styles.leftPanel} range={2} gap={0}>
              <LibrariesPanelWrapper onChooseLibrary={handleChooseLibrary} className={styles.contentLibraryPanel}>
                {getLibraryPanelComponent(chosenLibrary, false, handleChooseImage, undefined, setChosenImage)}
              </LibrariesPanelWrapper>
            </Section>
            <Section className={styles.centralPanel} range={6} gap={0}>
              {!!module && (
                <>
                  {/* <ModuleHeader
                    dirty={isModuleChanged}
                    onSave={handleModuleSave}
                    onClose={handleModuleClose}
                    className={styles.moduleHeader}
                  /> */}
                  <ModuleModal
                    modalTopPosition={10}
                    isTemplateEditor={true}
                    moduleHtmlElem={moduleHtml}
                    isEditorChanged={isEditorChanged}
                    setIsEditorChanged={setIsEditorChanged}
                    setRightPanelElem={setRightPanelElem}
                    onChange={handleModuleChange}
                    onClose={handleModuleClose}
                    onInputBlur={handleInputBlur}
                    templateStyles={templateStyles}
                  />
                </>
              )}
            </Section>
            <Section className={styles.rightPanel} range={4} gap={0}>
              <Box className={styles.rightPanelContent}>{getRightPanelElem()}</Box>
            </Section>
          </TreatmentBuilderContext.Provider>
        </DragDropContext>
        {showRenameModuleModal && (
          <RenameModuleModal
            title={textTemplate(content.renameValue, { value: content.contentModule })}
            alertMessage={content.itsNotPossibleRenameContentModule}
            moduleData={{
              moduleTemplateName: `${contentModuleName}`,
              deployments: moduleDeployments,
              hasBeenUsedAroundTemplates: module?.hasBeenUsedAroundTemplates,
              isRenameRequired: experienceData?.type === 'revision',
            }}
            type="module"
            onClose={handleRenameModuleModalClose}
            onSubmit={handleConfirmRenameModule}
          />
        )}
      </Box>
    </>
  );
};
